import { z } from 'zod';
import {
  BUSINESS_TYPE_CODES,
  CARRIER_CODE_TYPES,
  CUSTOMER_TYPE_CODES,
  GRACE_STATUSES_AFTER,
  GRACE_STATUSES_DURING,
  NLAD_SERVICE_TYPES,
  PLAN_STATE_STATUSES,
  PLAN_TYPES,
  SALES_CHANNELS,
} from '../enums';

/**
 * A company's starting set-up in one file (owner's request, 2026-10-30): carriers and their
 * wholesale codes, plans, top-ups, models, the service area, notification templates and settings.
 * Settings > Get started loads it, so an MVNO moving to 212Ez only drops in its credentials.
 * Importing adds what the company does not have; it never changes what is there. It holds no
 * credentials and no customers.
 */

const code = z.string().trim().min(1).max(40);
const state = z
  .string()
  .trim()
  .regex(/^[A-Z]{2}$/, 'A two-letter state');
const cents = z.number().int().min(0);

export const setupFileSchema = z.object({
  version: z.literal(1),
  source: z.string().trim().max(120).default(''),
  carriers: z
    .array(
      z.object({
        code,
        name: z.string().trim().min(1).max(80),
        codes: z
          .array(
            z.object({
              code,
              type: z.enum(CARRIER_CODE_TYPES).default('Service Plan'),
              voiceMinutes: z.number().int().min(0).nullable().default(null),
              textCount: z.number().int().min(0).nullable().default(null),
              dataMb: z.number().int().min(0).nullable().default(null),
              billingCode: z.string().trim().max(40).default(''),
            }),
          )
          .max(500)
          .default([]),
      }),
    )
    .max(50)
    .default([]),
  plans: z
    .array(
      z.object({
        /** The plan code it has today; a new one is given when it is taken or absent. */
        code: z.number().int().positive().optional(),
        name: z.string().trim().min(1).max(100),
        description: z.string().trim().max(200).default(''),
        type: z.enum(PLAN_TYPES),
        carrier: code,
        wholesaleCode: code,
        priceCents: cents,
        activationFeeCents: cents.default(0),
        channels: z.array(z.enum(SALES_CHANNELS)).min(1).default(['CSR', 'AGENT']),
        taxable: z.boolean().default(true),
        customerType: z.enum(CUSTOMER_TYPE_CODES).default('01'),
        businessType: z.enum(BUSINESS_TYPE_CODES).default('14'),
        priceIncludesTax: z.boolean().default(false),
        /** The price split over tax codes: percent and the tax engine's code. */
        taxLines: z
          .array(
            z.object({
              amountOrPercent: z.string().trim().max(12),
              transCode: z.string().trim().max(20),
            }),
          )
          .max(3)
          .default([]),
        isUnlimited: z.boolean().default(false),
        unlimitedData: z.boolean().default(false),
        unlimitedText: z.boolean().default(false),
        unlimitedMinutes: z.boolean().default(false),
        tribal: z.boolean().nullable().default(null),
        display: z.boolean().default(true),
        notifyCustomer: z.boolean().default(true),
        nladServiceType: z.enum(NLAD_SERVICE_TYPES).default('Voice'),
        gracePeriodDays: z.number().int().min(0).default(0),
        statusDuringGrace: z.enum(GRACE_STATUSES_DURING).default('Suspend'),
        statusAfterGrace: z.enum(GRACE_STATUSES_AFTER).default('Suspend'),
        states: z.array(state).min(1).max(60),
        status: z.enum(PLAN_STATE_STATUSES).default('LIVE'),
      }),
    )
    .max(500)
    .default([]),
  topups: z
    .array(
      z.object({
        name: z.string().trim().min(1).max(100),
        carrier: code,
        wholesaleCode: code,
        costCents: cents,
        voiceMinutes: z.number().int().min(0).default(0),
        textCount: z.number().int().min(0).default(0),
        dataMb: z.number().int().min(0).default(0),
        states: z.array(state).max(60).default([]),
      }),
    )
    .max(200)
    .default([]),
  /** One row per ZIP, kept short: state, ZIP, tribal, Lifeline, non-Lifeline, EBB. */
  serviceArea: z
    .array(
      z.tuple([
        state,
        z.string().regex(/^\d{5}$/, 'A five-digit ZIP'),
        z.boolean(),
        z.boolean(),
        z.boolean(),
        z.boolean(),
      ]),
    )
    .max(60_000)
    .default([]),
  /** Manage Model: what stock is added under. An imported customer's SIM needs a SIM model. */
  models: z
    .array(
      z.object({
        make: z.string().trim().min(1).max(60),
        modelLabel: z.string().trim().min(1).max(60),
        isSim: z.boolean().default(false),
        amountCents: cents.default(0),
        sku: z.string().trim().max(40).default(''),
        networkType: z.string().trim().max(20).default(''),
        simType: z.string().trim().max(20).default(''),
        os: z.string().trim().max(20).default(''),
      }),
    )
    .max(200)
    .default([]),
  notifications: z
    .array(
      z.object({
        channel: z.enum(['email', 'sms']),
        eventName: z.string().trim().min(1).max(120),
        categoryName: z.string().trim().max(80).default(''),
        smsName: z.string().trim().max(80).default(''),
        subject: z.string().trim().max(200).default(''),
        body: z.string().trim().min(1).max(20_000),
        status: z.enum(['Active', 'Inactive']).default('Active'),
        fromName: z.string().trim().max(100).default(''),
        fromEmail: z.string().trim().max(200).default(''),
        replyTo: z.string().trim().max(200).default(''),
        bccEmail: z.string().trim().max(200).default(''),
      }),
    )
    .max(300)
    .default([]),
  /** System Config values by key; a key the catalogue does not know is refused. */
  settings: z.record(z.union([z.boolean(), z.number(), z.string()])).default({}),
});
export type SetupFile = z.output<typeof setupFileSchema>;

/** What Load a sample set-up made: where to try it. */
export interface SampleSetupDto {
  zips: string[];
  sims: string[];
}

/** What Remove the sample set-up did. `kept`: a sale used it, so it is retired, not deleted. */
export interface SampleRemovedDto {
  plans: number;
  topups: number;
  sims: number;
  zips: number;
  kept: boolean;
}

/** What an import adds and leaves alone. `dryRun` answers this without changing anything. */
export interface SetupImportDto {
  dryRun: boolean;
  source: string;
  sections: Array<{ name: string; add: number; keep: number }>;
  /** Plans or top-ups that could not be placed, and why. */
  problems: string[];
}
